import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { FINANCE_QUERY_ROOTS } from './finance/queries'
import type { QueryClient } from '@tanstack/react-query'

/** Bumped on every build, so a deploy never reads a cache shaped for old code. */
declare const __BUILD_ID__: string

const KEY = 'medulla.query-cache'

/**
 * The last answers the server gave, kept across a refresh.
 *
 * This is what removes the spinner: on reload the board, the project list and
 * the rest paint from the cache on the first frame, and the fetch that follows
 * quietly replaces them. Nothing waits on the network to show something.
 *
 * Two rules hold it together, and both matter more than the speed:
 *
 * - **The session is never persisted.** The cookie is the only thing that may
 *   survive a reload, so the browser can never believe it is signed in when
 *   the server disagrees. Everything here is data the session is checked for
 *   before it is shown, and every endpoint checks again anyway.
 * - **It is cleared when the session changes.** Signing out wipes it, and so
 *   does signing in, so the next person at a shared machine never sees a
 *   frame of the last person's work.
 */
export const persister = createSyncStoragePersister({
  key: KEY,
  // Reads and writes are wrapped, because storage throws rather than returning
  // null in a private window or with site data blocked - and a cache that
  // cannot be written is not a reason to fail to start.
  storage: {
    getItem: (key) => {
      try {
        return window.localStorage.getItem(key)
      } catch {
        return null
      }
    },
    setItem: (key, value) => {
      try {
        window.localStorage.setItem(key, value)
      } catch {
        // Out of quota, or storage denied. The app works without it.
      }
    },
    removeItem: (key) => {
      try {
        window.localStorage.removeItem(key)
      } catch {
        // As above.
      }
    },
  },
  // Coalesced, so a board that refetches on every keystroke of a filter does
  // not serialise the whole cache each time.
  throttleTime: 1_000,
})

export const persistOptions = {
  persister,
  /** A day. Older than that and a fetch is cheaper than a wrong first frame. */
  maxAge: 1000 * 60 * 60 * 24,
  buster: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev',
  dehydrateOptions: {
    // Never the session, and never anything decrypted from finance: that
    // lives in memory only, for as long as finance is unlocked.
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) =>
      query.state.status === 'success' && query.queryKey[0] !== 'session' && !(FINANCE_QUERY_ROOTS as readonly unknown[]).includes(query.queryKey[0]),
  },
}

/**
 * Forgets everything, in memory and on disk.
 *
 * Called on the way in and on the way out. On the way out for the obvious
 * reason; on the way in because the person signing in may not be the person
 * who signed out.
 */
export function forgetEverything(queryClient: QueryClient): void {
  queryClient.clear()
  void persister.removeClient()
}
